import React from 'react';
import Editor from '@monaco-editor/react';

interface CodeEditorProps {
  language: string;
  code: string;
  onLanguageChange: (language: string) => void;
  onCodeChange: (code: string) => void;
  getInitialCode: (lang: string) => string;
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  language,
  code,
  onLanguageChange,
  onCodeChange,
  getInitialCode,
}) => {
  const handleEditorChange = (value: string | undefined) => {
    onCodeChange(value || '');
  };

  const handleLanguageChange = (newLanguage: string) => {
    onLanguageChange(newLanguage);
    onCodeChange(getInitialCode(newLanguage));
  };

  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'cpp', label: 'C++' },
  ];

  return (
    <div className='h-full flex flex-col bg-white rounded-lg shadow-lg'>
      <div className='flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg'>
        <div className='flex items-center gap-4'>
          <h3 className='text-lg font-semibold text-gray-800'>Code Editor</h3>
          <select
            value={language}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className='px-3 py-1 border border-gray-300 rounded text-sm bg-white'
          >
            {languages.map((lang) => (
              <option
                key={lang.value}
                value={lang.value}
              >
                {lang.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='flex-1 overflow-hidden'>
        <Editor
          height='100%'
          language={language}
          value={code}
          onChange={handleEditorChange}
          theme='vs-light'
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            roundedSelection: false,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            // Disable suggestions/IntelliSense
            quickSuggestions: false,
            suggestOnTriggerCharacters: false,
            acceptSuggestionOnEnter: 'off',
            tabCompletion: 'off',
            wordBasedSuggestions: 'off',
            // Disable parameter hints
            parameterHints: { enabled: false },
            // Disable hover information
            hover: { enabled: false },
            // Disable right-click context menu
            contextmenu: false,
          }}
        />
      </div>
    </div>
  );
};

export default CodeEditor;
